import { useEffect, useRef, useState } from 'react';
import { saveAs } from 'file-saver';
import Papa from 'papaparse';
import { toast } from 'react-toastify';
import { apiService } from '../../services/apiService'; // matches src/components/exporttable/

const DEFAULT_THRESHOLD = 10000;
const AUTO_HIDE_MS = 10000; // preparing popup stays open 10s; work keeps running
const MODAL_SWAP_MS = 300;  // let the "Large export" popup finish closing first

// YYYY-MM-DD for the emailed file name (e.g. my_tasks_export_2026-06-16.xlsx)
const today = () => new Date().toISOString().slice(0, 10);

/**
 * @param {string}   cfg.endpoint      export endpoint -> { success, data: [...] }
 * @param {string}   cfg.endpointMail  mail endpoint hit on "Email me instead"
 *                                     (e.g. '/send-mail-once-exported-the-tasks')
 * @param {string}   [cfg.mailFileBase] base for the email file_name; final value is
 *                                      `${mailFileBase}_${YYYY-MM-DD}.xlsx`.
 *                                      Falls back to `fileName` if omitted.
 * @param {Function} cfg.mapRow        (rawRecord) => flat row object
 * @param {string}   [cfg.fileName]    CSV file name for the browser download
 * @param {number}   [cfg.threshold]   record count above which the email prompt shows
 */
export function useTableExport({
    endpoint,
    endpointMail,
    mailFileBase,
    mapRow,
    fileName = 'export.csv',
    threshold = DEFAULT_THRESHOLD,
}) {
    const [exporting, setExporting] = useState(false); // -> "preparing" popup
    const [prompt, setPrompt] = useState(null);        // -> "Large export" popup (download only)

    const mountedRef = useRef(true);
    const hideTimerRef = useRef(null);

    useEffect(() => {
        // reset to true on every mount (React 18 StrictMode mounts twice)
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
        };
    }, []);

    const safeSetExporting = (v) => {
        if (mountedRef.current) setExporting(v);
    };

    // show the preparing popup for a fixed 10s; the request runs independently,
    // so the file still downloads/copies in the background (even after navigation)
    const showPreparing = () => {
        safeSetExporting(true);
        if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
        hideTimerRef.current = setTimeout(() => safeSetExporting(false), AUTO_HIDE_MS);
    };

    // POST { count, file_name } to the mail endpoint -> backend emails the export
    const triggerEmailNotify = (count) => {
        if (!endpointMail) {
            console.warn('useTableExport: no endpointMail provided; skipping email notify.');
            return;
        }
        const file_name = mailFileBase
            ? `${mailFileBase}_${today()}.xlsx`
            : fileName;

        apiService
            .postRequest(endpointMail, { count, file_name })
            .then(() => toast('We will email you a copy shortly.'))
            .catch((err) => console.error('Email notify error:', err));
    };

    // core: pull all records, then download / copy. NOT aborted on unmount.
    const doExport = async (action, params, { notifyEmail = false } = {}) => {
        showPreparing();
        try {
            const res = await apiService.postRequest(endpoint, params);

            if (res?.success === true) {
                const csvData = (res.data || []).map(mapRow);
                if (!csvData.length) {
                    toast('No records to export.');
                    return;
                }

                if (action === 'download') {
                    const csv = Papa.unparse(csvData);
                    saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), fileName);
                    toast('Your download is ready.');

                    // "Email me instead" path: after the browser download, hit the
                    // mail endpoint with { count, file_name }
                    if (notifyEmail) triggerEmailNotify(csvData.length);
                } else {
                    const headers = Object.keys(csvData[0]).join('\t');
                    const dataRows = csvData.map((row) =>
                        Object.values(row).map((v) => v ?? '').join('\t')
                    );
                    await navigator.clipboard.writeText([headers, ...dataRows].join('\n'));
                    toast('Copied! Paste directly into Excel or Google Sheets.');
                }
            } else {
                toast.error('Export failed. Please try again.');
            }
        } catch (err) {
            console.error('Export error:', err);
            toast.error('Export failed. Please try again.');
        }finally{
            setExporting(false)
        }
        // NOTE: no setExporting(false) here — the 10s timer owns hiding the popup,
        // so it always shows for the full countdown while work runs in background.
    };

    const requestExport = (action, { totalCount = 0, params }) => {
        // Large export popup only for DOWNLOAD over the threshold. Copy never prompts.
        if (action === 'download' && Number(totalCount) > threshold) {
            setPrompt({ params });
            return;
        }
        doExport(action, params, { notifyEmail: false });
    };

    // close the Large export popup FIRST, then open preparing after the transition
    const confirmEmail = () => {
        const p = prompt;
        setPrompt(null);
        if (p) setTimeout(() => doExport('download', p.params, { notifyEmail: true }), MODAL_SWAP_MS);
    };
    const confirmContinue = () => {
        const p = prompt;
        setPrompt(null);
        if (p) setTimeout(() => doExport('download', p.params, { notifyEmail: false }), MODAL_SWAP_MS);
    };
    const cancelPrompt = () => setPrompt(null);

    return {
        exporting,
        prompt,
        requestExport,
        confirmEmail,
        confirmContinue,
        cancelPrompt,
        threshold,
    };
}